import { useState } from 'react';
import Swal from 'sweetalert2';
import type { SavedEventEntry } from '@/mocks/listenerCenter';

interface Props {
  events: SavedEventEntry[];
}

export default function SavedEvents({ events }: Props) {
  const [items, setItems] = useState(events);

  const upcoming = items.filter((e) => e.isUpcoming);
  const past = items.filter((e) => !e.isUpcoming);

  const confirmRemove = (id: number, title: string) => {
    Swal.fire({
      title: 'Eliminar evento',
      text: `¿Querés eliminar "${title}" de tus eventos guardados?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setItems(items.filter((e) => e.id !== id));
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Evento eliminado de guardados',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      }
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const eventTypeColors: Record<string, string> = {
    'Especial': 'bg-amber-100 text-amber-700',
    'Concierto': 'bg-purple-100 text-purple-700',
    'Deportivo': 'bg-sky-100 text-sky-700',
    'Festival': 'bg-rose-100 text-rose-700',
  };

  return (
    <div className="space-y-8">
      {/* Upcoming Events */}
      <div>
        <h3 className="text-sm font-semibold text-foreground-950 mb-4">
          Próximos Eventos ({upcoming.length})
        </h3>
        {upcoming.length === 0 ? (
          <div className="bg-background-50 rounded-2xl p-10 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-calendar-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-600 text-sm">No tienes eventos próximos</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {upcoming.map((event) => (
              <div key={event.id} className="bg-background-50 rounded-xl overflow-hidden group">
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={event.imageUrl}
                    alt={event.eventTitle}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${eventTypeColors[event.eventType] || 'bg-foreground-100 text-foreground-600'}`}>
                      {event.eventType}
                    </span>
                  </div>
                  <button
                    onClick={() => confirmRemove(event.id, event.eventTitle)}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-950/40 hover:bg-foreground-950/60 text-background-50 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h4 className="text-sm font-bold text-background-50 leading-tight">{event.eventTitle}</h4>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-radio-line"></i>
                    <span>{event.stationName}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-600">
                    <i className="ri-calendar-check-line text-accent-500"></i>
                    <span className="font-medium">{formatDate(event.eventDate)}</span>
                    <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                    <i className="ri-time-line"></i>
                    <span>{event.eventTime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Events */}
      {past.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground-950 mb-4">
            Eventos Pasados ({past.length})
          </h3>
          <div className="space-y-2">
            {past.map((event) => (
              <div key={event.id} className="bg-background-50 rounded-xl p-4 flex items-center gap-4 group">
                <img
                  src={event.imageUrl}
                  alt={event.eventTitle}
                  className="w-14 h-14 rounded-lg object-cover object-top shrink-0 opacity-75"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-foreground-950">{event.eventTitle}</h4>
                  <div className="flex items-center gap-2 text-xs text-foreground-500 mt-0.5">
                    <span>{event.stationName}</span>
                    <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                    <span>{formatDate(event.eventDate)}</span>
                  </div>
                </div>
                <button
                  onClick={() => confirmRemove(event.id, event.eventTitle)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}